import { Button } from '@ixicare/ui';
import * as React from 'react';
import AlertResolveModal from './AlertResolveModal';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { t } from '~i18n';
import styles from './AlertActions.module.css';
import { AlertStatus, Profile } from '@ixicare/resources';
import AlertRejectModal from './AlertRejectModal';
import { useQuery } from '@tanstack/react-query';
import classnames from 'classnames';
import AlertCallModal from './AlertCallModal';
import { useStores } from '~store/utils';
import { UserAction } from '~config/notification';

interface AlertActionsContainerProps {
  alert: Alert;
}

type ActionType = 'resolve' | 'reject' | 'call';

const AlertActionsContainer: React.FC<AlertActionsContainerProps> = ({ alert }) => {
  const { auth, notifications, permissions } = useStores();
  // Multi-site viewers (those with the can_view_site permission) can browse other sites' alarms but may only
  // act on their own site. A single-site profile therefore keeps full access on their own site.
  const isRemoteViewerType = permissions.has('can_view_site');
  // siteUuid is null until the detail fetch lands — treat "unknown" as not-own-site (fail safe to read-only).
  const isOwnSite = !!alert.siteUuid && alert.siteUuid === auth.user?.site?.uuid;
  const isReadOnly = isRemoteViewerType && !isOwnSite;
  const [actionType, setActionType] = React.useState<ActionType>();
  const [loading, setLoading] = React.useState(false);

  const notifyActionError = React.useCallback(
    (err: any) => {
      console.error(err);
      notifications.addUserActionNotification({
        type: UserAction.AlarmAction,
        notificationType: 'error',
        message: t('alert.actions.error'),
      });
    },
    [notifications]
  );

  const handleReject = async (reason: string) => {
    try {
      setLoading(true);
      await alert.reject(reason);
      setActionType(undefined);
    } catch (err) {
      notifyActionError(err);
    }
    setLoading(false);
  };

  const handleAccept = async () => {
    try {
      setLoading(true);
      await alert.accept();
      setActionType(undefined);
    } catch (err) {
      notifyActionError(err);
    }
    setLoading(false);
  };

  const handleResolve = async (reason: string) => {
    try {
      setLoading(true);
      await alert.resolve(reason);
      setActionType(undefined);
    } catch (err) {
      notifyActionError(err);
    }
    setLoading(false);
  };

  const { data: resident } = useQuery({
    queryKey: ['profile', alert.resident?.id],
    queryFn: () => Profile.getProfile(alert.resident?.id ?? -1),
    enabled: !!alert.resident?.id,
  });

  const phoneNumbers = React.useMemo(() => {
    const data: { label: string; value: string }[] = [];
    if (alert.device?.phoneNumber) {
      data.push({ label: t('alert.call.devicePhoneNumber'), value: alert.device?.phoneNumber });
    }
    if (resident?.phoneNumber) {
      data.push({ label: t('alert.call.residentPrimaryPhoneNumber'), value: resident.phoneNumber });
    }
    if (resident?.secondaryPhoneNumbers) {
      data.push({ label: t('alert.call.residentSecondaryPhoneNumber'), value: resident.secondaryPhoneNumbers });
    }
    return data;
  }, [alert, resident]);
  const canCall = alert.isOpen && phoneNumbers.length;

  const handleCall = React.useCallback(() => {
    if (!alert.isAccepted) {
      alert.accept();
    }
    alert.allowUserToResolve();
  }, [alert]);

  return (
    <>
      <div className={styles.AlertActions}>
        <Button
          className={styles.ActionButton}
          disabled={isReadOnly || alert.isRejected || alert.status !== AlertStatus.Open}
          loading={loading}
          onClick={() => setActionType('reject')}
          buttonType="error"
          icon="cross"
        >
          {t('alert.actions.decline')}
        </Button>
        <div style={{ display: 'flex' }}>
          <Button
            className={classnames(styles.ActionButton, 'mr-5')}
            disabled={isReadOnly || !canCall}
            onClick={() => setActionType('call')}
            icon="voice-communication"
            buttonType="inverse"
          >
            {t('alert.actions.call')}
          </Button>
          {alert.status === AlertStatus.Accepted ? (
            <Button
              className={styles.ActionButton}
              buttonType="success"
              disabled={isReadOnly}
              loading={loading}
              onClick={() => setActionType('resolve')}
              icon="check"
            >
              {t('alert.actions.resolve')}
            </Button>
          ) : (
            <Button
              className={styles.ActionButton}
              buttonType="success"
              disabled={isReadOnly || alert.status !== AlertStatus.Open}
              loading={loading}
              onClick={handleAccept}
              icon="check"
            >
              {t('alert.actions.accept')}
            </Button>
          )}
        </div>
      </div>

      {actionType === 'resolve' && (
        <AlertResolveModal
          alert={alert}
          onClose={() => setActionType(undefined)}
          onResolve={handleResolve}
          loading={loading}
        />
      )}
      {actionType === 'reject' && (
        <AlertRejectModal onClose={() => setActionType(undefined)} onReject={handleReject} loading={loading} />
      )}
      {actionType === 'call' && (
        <AlertCallModal phoneNumbers={phoneNumbers} onClose={() => setActionType(undefined)} onCall={handleCall} />
      )}
    </>
  );
};

export default observer(AlertActionsContainer);
